import React from 'react';
import { Link } from 'react-router-dom';

/*
 * /faq — rebuilt 2 Sep 2026 on the landing-page baseline.
 *
 * The previous version was eighteen questions written for the old model and it
 * had drifted badly out of date: it used "advisor" throughout, which is retired
 * terminology; it described subscribing to an advisor and changing advisors,
 * which is the practitioner-marketplace shape we have refused; and it addressed
 * only individuals, on a site whose customer is now the employer.
 *
 * Grouped by who is asking, because the two audiences want different things and
 * a single list made an employer read fifteen consumer questions first.
 *
 * Answers point at the page that carries the detail rather than restating it —
 * one fact, one home. Claims discipline as everywhere: no prevention, no
 * outcome, no saving, and cost questions go to the calculator where the sources
 * are shown.
 */

const QA = ({ q, children }) => (
    <div className="qa">
        <h3>{q}</h3>
        <p>{children}</p>
    </div>
);

const FAQPage = () => (
    <div className="pa2">

        <section className="hero" id="top">
            <div className="wrap">
                <h1 className="on-dark">
                    <span className="setup">Questions,</span>
                    <span className="payoff">answered plainly.</span>
                </h1>
                <p className="lede">
                    If something here is not clear enough, ask us. We reply to every enquiry.
                </p>
                <div className="cta-row">
                    <Link className="btn btn-t" to="/contact">Contact us</Link>
                </div>
            </div>
        </section>

        <section className="tint" id="employers">
            <div className="wrap">
                <h2><span className="setup">If you are an employer.</span></h2>
                <div className="faq two-up">
                    <QA q="What are we actually buying?">
                        Coverage. Everyone you provision gets a physiotherapy programme personalised
                        by a registered physiotherapist, from day one, with no referral and nothing
                        for them to claim. You also get reporting on how much of it is happening.{' '}
                        <Link to="/employers">What that looks like</Link>.
                    </QA>
                    <QA q="What can we see about our staff?">
                        Nothing about any individual. Take-up and adherence at workforce level, never
                        names, symptoms, programmes or activity, and nothing for any group under
                        fifty people. The boundary is enforced in the product.{' '}
                        <Link to="/trust-centre">Trust centre</Link>.
                    </QA>
                    <QA q="What does it cost?">
                        Pricing is set with you rather than published, because it depends on
                        headcount and what you provision. What is published is what MSK Health is
                        costing you already, from national figures and your own headcount.{' '}
                        <Link to="/contact">See the estimate</Link>.
                    </QA>
                    <QA q="Will it reduce our absence?">
                        We do not claim that, because we cannot yet evidence it. What we commit to
                        is coverage and time to support; adherence we report rather than promise;
                        outcomes and savings we do not assert at all.{' '}
                        <Link to="/trust-centre#promise">What we commit to</Link>.
                    </QA>
                    <QA q="How long does it take to set up?">
                        Provisioning is straightforward and does not need an integration project.
                        Tell us your headcount and how you want people invited, and we will give you
                        a timeline for your situation rather than a marketing number.
                    </QA>
                    <QA q="Is this a replacement for occupational health?">
                        No. Occupational health is referral-triggered and deals with cases. This
                        measures everyone continuously, which is a different job. Most organisations
                        that have one will keep it.
                    </QA>
                </div>
            </div>
        </section>

        <section className="white" id="individuals">
            <div className="wrap">
                <h2><span className="setup">If it is for you.</span></h2>
                <div className="faq two-up">
                    <QA q="Do I need an injury to use it?">
                        No, and most people using it do not have one. You do not need a problem to
                        be worth measuring.{' '}
                        <Link to="/individuals">How it works</Link>.
                    </QA>
                    <QA q="Do I need a smartwatch?">
                        No. The phone you already carry is enough to build a first programme from. A
                        wearable adds heart rate and sleep and makes the picture richer, and the
                        programme uses it if you have one.
                    </QA>
                    <QA q="Can my employer see my data?">
                        No. Not your name, not your symptoms, not your programme, not your activity.
                        They see workforce-level take-up and nothing below a group of fifty.{' '}
                        <Link to="/trust-centre">The full boundary</Link>.
                    </QA>
                    <QA q="Who writes my programme?">
                        A physiotherapist registered with the HCPC, the UK statutory regulator. You
                        can see who they are and their registration number in the app.{' '}
                        <Link to="/blog/who-writes-your-programme">How programmes are made</Link>.
                    </QA>
                    <QA q="What happens if I leave my job?">
                        The account is yours and it stays with you. Your employer is not told that
                        you kept it.
                    </QA>
                    <QA q="Can I use this alongside NHS care?">
                        Yes. This is supplementary guidance, not a replacement for care through the
                        NHS, your GP or emergency services. If something is seriously wrong, contact
                        your healthcare provider.
                    </QA>
                    <QA q="How do I delete my data?">
                        From the app, at any time, and deletion is permanent. You can export it
                        first if you want a copy.
                    </QA>
                    <QA q="My employer does not offer this. Can I still use it?">
                        Yes, and you can also ask us to approach them.{' '}
                        <Link to="/individuals#employer">Is my employer covered?</Link>
                    </QA>
                </div>
            </div>
        </section>

        <section className="tint" id="physios">
            <div className="wrap">
                <h2><span className="setup">If you are a physiotherapist.</span></h2>
                <div className="faq two-up">
                    <QA q="How do I get access?">
                        Through the form. Access is provisioned by us after we verify your registration. It is not open sign-up, and there is no self-serve listing.{' '}
                        <Link to="/physiotherapists">Partner with us</Link>.
                    </QA>
                    <QA q="Does the software decide the programme?">
                        No. It proposes adjustments from movement data; you decide what the person
                        actually does, and that decision is recorded against you.{' '}
                        <Link to="/cmpx#roles">What the platform does</Link>.
                    </QA>
                </div>
            </div>
        </section>

        <section className="white" id="researchers">
            <div className="wrap">
                <h2><span className="setup">If you are running a study.</span></h2>
                <div className="faq two-up">
                    <QA q="What data could we actually get?">
                        Movement collected the same way for everyone, which is what makes it
                        comparable across a cohort rather than only within one. De-identified,
                        with no route back to an individual.{' '}
                        <Link to="/cmpx#roles">What the platform provides</Link>.
                    </QA>
                    <QA q="How does consent work?">
                        Research use is opt-in, asked separately from the programme, and reversible
                        at any point without affecting anyone&rsquo;s care. Nobody is enrolled into
                        a study by being a user.
                    </QA>
                    <QA q="Can we recruit and follow people over time?">
                        Yes. Participants can be enrolled and followed without asking them to keep
                        a diary, which is usually where longitudinal data goes wrong.
                    </QA>
                    <QA q="What do we need in place first?">
                        A data sharing agreement, and ethics approval for whatever you are running.
                        We do not share data outside the agreement that authorised it.{' '}
                        <Link to="/researchers">For researchers</Link> covers how to start.
                    </QA>
                </div>
            </div>
        </section>

        <section className="s-promise" id="ask">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Not answered here?</span>
                </h2>
                <p className="lede">
                    Ask. Tell us whether you are asking for an organisation or for yourself and we
                    will reply accordingly.
                </p>
                <div className="cta-row">
                    <Link className="btn btn-t" to="/contact">Contact us</Link>
                </div>
            </div>
        </section>

    </div>
);

export default FAQPage;
